/**
 * WCAG contrast primitives, shared by the text-contrast lint rule. Pure colour math — no DOM.
 */

export interface RGB {
    r: number;
    g: number;
    b: number;
    /** Alpha 0..1, set by parseColor (1 when the source carries no alpha). Absent for bare colours. */
    a?: number;
}

const AA_BODY = 4.5;
const AA_LARGE = 3;
// WCAG "large text": >= 18pt (24px), or >= 14pt bold (~18.66px).
const LARGE_PX = 24;
const LARGE_BOLD_PX = 18.66;

/** Parse an `rgb()`/`rgba()` or `#rgb`/`#rrggbb` colour, capturing alpha. Null if unparseable. */
export function parseColor(value: string): RGB | null {
    const rgbMatch = value.match(
        /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/i,
    );
    if (rgbMatch) {
        return {
            r: Number(rgbMatch[1]),
            g: Number(rgbMatch[2]),
            b: Number(rgbMatch[3]),
            a: rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4]),
        };
    }
    const hex = value.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
        const h = hex[1];
        const full = h.length === 3 ? h.replace(/(.)/g, '$1$1') : h;
        return {
            r: parseInt(full.slice(0, 2), 16),
            g: parseInt(full.slice(2, 4), 16),
            b: parseInt(full.slice(4, 6), 16),
            a: 1,
        };
    }
    return null;
}

function relativeLuminance({ r, g, b }: RGB): number {
    const channel = (value: number): number => {
        const c = value / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** The WCAG contrast ratio between two colours (1..21), symmetric in its arguments. */
export function contrastRatio(a: RGB, b: RGB): number {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const lighter = Math.max(la, lb);
    const darker = Math.min(la, lb);
    return (lighter + 0.05) / (darker + 0.05);
}

/** Whether text of the given size/weight counts as "large" under WCAG. */
export function isLargeText(fontSizePx: number, bold: boolean): boolean {
    return fontSizePx >= LARGE_PX || (bold && fontSizePx >= LARGE_BOLD_PX);
}

/** Whether a contrast ratio meets WCAG AA for the given text size. */
export function meetsAA(ratio: number, large: boolean): boolean {
    return ratio >= (large ? AA_LARGE : AA_BODY);
}

export interface ImageLike {
    data: Uint8ClampedArray | number[];
    width: number;
    height: number;
}

interface SampleRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

// Manhattan distance below which a sampled pixel is treated as the text's own ink, not background.
const INK_DISTANCE = 40;

/**
 * Estimate the background colour behind a glyph region by sampling a grid of pixels inside it and
 * averaging those that are not the text's own ink (near the foreground colour). When EVERY sample
 * reads as ink, the text has no distinct background — it blends into a same-coloured fill, the
 * worst-case contrast — so the sampled (foreground-matching) colour is returned rather than null, so
 * the rule still flags it. Null only when the region is entirely off-canvas. This is what makes
 * contrast correct over images and gradients, where the background can't be derived from CSS.
 */
export function sampleBackgroundUnderGlyph(
    image: ImageLike,
    rect: SampleRect,
    foreground: RGB,
    gridSize = 6,
): RGB | null {
    const { data, width, height } = image;
    const x0 = Math.max(0, Math.floor(rect.x));
    const y0 = Math.max(0, Math.floor(rect.y));
    const x1 = Math.min(width - 1, Math.floor(rect.x + rect.width));
    const y1 = Math.min(height - 1, Math.floor(rect.y + rect.height));
    if (x1 < x0 || y1 < y0) return null;

    const step = Math.max(1, gridSize - 1);
    let sumR = 0;
    let sumG = 0;
    let sumB = 0;
    let count = 0; // non-ink samples
    let allR = 0;
    let allG = 0;
    let allB = 0;
    let total = 0; // every sampled pixel
    for (let i = 0; i < gridSize; i++) {
        for (let j = 0; j < gridSize; j++) {
            const px = x0 + Math.round(((x1 - x0) * i) / step);
            const py = y0 + Math.round(((y1 - y0) * j) / step);
            const idx = (py * width + px) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            allR += r;
            allG += g;
            allB += b;
            total++;
            const inkDistance =
                Math.abs(r - foreground.r) +
                Math.abs(g - foreground.g) +
                Math.abs(b - foreground.b);
            if (inkDistance < INK_DISTANCE) continue; // the glyph's own ink, not background
            sumR += r;
            sumG += g;
            sumB += b;
            count++;
        }
    }
    if (total === 0) return null;
    // No non-ink pixel found: the region is uniformly the foreground colour, so the text blends into
    // its background. Return that colour so the ratio is ~1:1 and the rule flags it.
    if (count === 0) {
        return {
            r: Math.round(allR / total),
            g: Math.round(allG / total),
            b: Math.round(allB / total),
        };
    }
    return {
        r: Math.round(sumR / count),
        g: Math.round(sumG / count),
        b: Math.round(sumB / count),
    };
}
